import { subscribe, TConnectionItem } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { useEffect, useMemo } from 'react';
import { client } from '../../config/api';
import { onSubscriptionData } from '../../config/cache';
import {
  GetGiftPackageDocument,
  OnAnyGiftPackageChangedDocument,
  TGetGiftPackageQuery,
  TGetGiftPackageQueryVariables,
  TGiftPackageFieldsFragment,
  TListDefaultGiftPackagesQuery,
  TListDefaultGiftPackagesQueryVariables,
  TListGiftPackagesQuery,
  TListGiftPackagesQueryVariables,
  TListOneCompanyGiftPackagesQuery,
  TListOneCompanyGiftPackagesQueryVariables,
  TListOneGiftPackageQuery,
  TListOneGiftQueryVariables,
  TListOneSubTypeGiftPackagesQuery,
  TListOneSubTypeGiftPackagesQueryVariables,
  TListOneTypeGiftPackagesQuery,
  TListOneTypeGiftPackagesQueryVariables,
  TOnAnyGiftPackageChangedSubscription,
  TOnAnyGiftPackageChangedSubscriptionVariables,
  useListDefaultGiftPackagesLazyQuery,
  useListGiftPackagesLazyQuery,
  useListOneCompanyGiftPackagesLazyQuery,
  useListOneGiftPackageLazyQuery,
  useListOneSubTypeGiftPackagesLazyQuery,
  useListOneTypeGiftPackagesLazyQuery
} from '../../generated/graphql';
import { useQueryAll } from '../shared/useQueryAll';

const queryGiftPackageOnSubscriptionData = async (data?: TOnAnyGiftPackageChangedSubscription, vars?: TOnAnyGiftPackageChangedSubscriptionVariables) => {
  const { id, _deleted } = data?.onAnyGiftPackageChanged || {};

  if (_deleted) return onSubscriptionData({ ...data } as Record<string, any>, vars);
  if (!id) return;

  const response = await client.query<TGetGiftPackageQuery, TGetGiftPackageQueryVariables>({
    query: GetGiftPackageDocument,
    variables: {
      id
    }
  });

  const packageData = response.data.getGiftPackage as TConnectionItem;

  if (packageData) {
    const subscriptionData = {
      onAnyGiftPackageChanged: packageData as TConnectionItem
    };

    // CHANGED DATA FOR QUERIES THAT DEPEND ON NO VARIABLE (SINCE VARS SHOULD BE AN EMPTY OBJECT IN THIS SITUATION)
    onSubscriptionData(subscriptionData, vars);
  }
};

let anyGiftPackageChangedSubscribed = false;

export const useSubscribeToGiftPackageChanged = () => {
  useEffect(() => {
    if (!anyGiftPackageChangedSubscribed)
      subscribe({
        client,
        query: OnAnyGiftPackageChangedDocument,
        onSubscriptionData: (data, vars) => {
          return queryGiftPackageOnSubscriptionData(data as TOnAnyGiftPackageChangedSubscription, vars as TOnAnyGiftPackageChangedSubscriptionVariables);
        }
      });

    anyGiftPackageChangedSubscribed = true;
  }, []);
};

// =========================
// NEW
// =========================

export type TGiftPackType = TGiftPackageFieldsFragment;

export type TCuratedPackageType = Pick<TGiftPackType, 'id' | '_version' | 'products'>;

export const useQueryAllGiftPackages = () => {
  useSubscribeToGiftPackageChanged();

  const { data, loading, error } = useQueryAll<TListGiftPackagesQuery, TListGiftPackagesQueryVariables>({
    useQuery: useListGiftPackagesLazyQuery,
    variables: { isScan: true }
  });

  const giftPackages = useMemo(() => data?.listGiftPackages.items || [], [data]);

  return {
    data: giftPackages as TGiftPackType[],
    loading,
    error
  };
};

export const useQueryDefaultGiftPackages = () => {
  useSubscribeToGiftPackageChanged();

  const { data, loading, error } = useQueryAll<TListDefaultGiftPackagesQuery, TListDefaultGiftPackagesQueryVariables>({
    useQuery: useListDefaultGiftPackagesLazyQuery
  });

  const giftPackages = useMemo(() => data?.listDefaultGiftPackages.items || [], [data]);

  return {
    data: giftPackages as TGiftPackType[],
    loading,
    error
  };
};

export const useQueryOneGiftPackage = (variables: TListOneGiftQueryVariables) => {
  useSubscribeToGiftPackageChanged();

  const { data, loading, error } = useQueryAll<TListOneGiftPackageQuery, TListOneGiftQueryVariables>({
    useQuery: useListOneGiftPackageLazyQuery,
    variables
  });

  const giftPackages = useMemo(() => data?.listOneGiftPackage.items[0] || {}, [data]);

  return {
    data: giftPackages as TGiftPackType,
    loading,
    error
  };
};

export const useQueryOneCompanyGiftPackages = (variables: TListOneCompanyGiftPackagesQueryVariables) => {
  useSubscribeToGiftPackageChanged();

  const { data, loading, error } = useQueryAll<TListOneCompanyGiftPackagesQuery, TListOneCompanyGiftPackagesQueryVariables>({
    useQuery: useListOneCompanyGiftPackagesLazyQuery,
    variables
  });

  const giftPackages = useMemo(() => data?.listOneCompanyGiftPackages.items || [], [data]);

  return {
    data: giftPackages as TGiftPackType[],
    loading,
    error
  };
};

export const useQueryOneTypeGiftPackages = (variables: TListOneTypeGiftPackagesQueryVariables) => {
  useSubscribeToGiftPackageChanged();

  const { data, loading, error } = useQueryAll<TListOneTypeGiftPackagesQuery, TListOneTypeGiftPackagesQueryVariables>({
    useQuery: useListOneTypeGiftPackagesLazyQuery,
    variables
  });

  const giftPackages = useMemo(() => data?.listOneTypeGiftPackages.items || [], [data]);

  return {
    data: giftPackages as TGiftPackType[],
    loading,
    error
  };
};

export const useQueryOneSubTypeGiftPackages = (variables: TListOneSubTypeGiftPackagesQueryVariables) => {
  useSubscribeToGiftPackageChanged();

  const { data, loading, error } = useQueryAll<TListOneSubTypeGiftPackagesQuery, TListOneSubTypeGiftPackagesQueryVariables>({
    useQuery: useListOneSubTypeGiftPackagesLazyQuery,
    variables
  });

  const giftPackages = useMemo(() => data?.listOneSubTypeGiftPackages.items || [], [data]);

  return {
    data: giftPackages as TGiftPackType[],
    loading,
    error
  };
};
